import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, MessageSquare, AlertCircle, Tag, Calendar, Download, Video, Users, Settings, Moon, Sun, Home, Bot, Shield, PlayCircle, Lock, Brain, DollarSign, FileText, GraduationCap, Network, CheckCircle, Info, ArrowRight, ChevronDown, ChevronUp, BookOpen, Zap, Award, FileCheck, ThumbsUp, ThumbsDown, Cloud, Package, Eye, ExternalLink, Clock } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { GlobalFooter } from './GlobalFooter';

/**
 * DESIGN RULE: NO WHITE TEXT ON THIS PAGE
 * - All text content must use black (text-black) or gray (text-gray-XXX)
 * - Exception: White text is ONLY allowed for button labels on colored button backgrounds
 * - Icons on colored backgrounds can use text-white (decorative only)
 * - Modal headers, descriptions, and body text must use black or gray
 * - This ensures readability and follows ORA's design system
 */

// Custom Tooltip Component with gray text instead of white
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-xs text-gray-700">
            <span style={{ color: entry.color }}>{entry.name}: </span>
            <span className="font-semibold">{entry.value}%</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Pie Chart Label with dark text displayed outside the pie chart
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 30; // Position outside the pie chart
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="#1f2937" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      className="font-semibold"
      style={{ fontSize: '14px' }}
    >
      {`${Math.ceil(percent * 100)}%`}
    </text>
  );
};

// Generate sample sentiment data matching the screenshot
const generateSentimentData = () => {
  const dates = ['Oct 19', 'Oct 23', 'Oct 27', 'Oct 31', 'Nov 4', 'Nov 8', 'Nov 12', 'Nov 16', 'Nov 20', 'Nov 28', 'Dec 2', 'Dec 6', 'Dec 10', 'Dec 14', 'Dec 18', 'Dec 22', 'Dec 30', 'Jan 3', 'Jan 6', 'Jan 15'];
  
  return dates.map((date, index) => {
    let negative, neutral, positive;
    
    // Negative trend: Start at 65%, stay around 60% then 50% for ~45 days, then decline to 30%
    if (index === 0) {
      negative = 65;
    } else if (index <= 2) {
      negative = 60 + Math.random() * 3;
    } else if (index <= 12) {
      // Stay around 50% for about 45 days (points 3-12)
      negative = 48 + Math.random() * 4;
    } else {
      // Decline from 50% to 30% (points 13-19)
      const declineProgress = (index - 13) / 6;
      negative = 50 - (declineProgress * 20) + Math.random() * 3;
    }
    
    // Positive trend: gradual increase
    const progress = index / (dates.length - 1);
    positive = 15 + progress * 60 + Math.random() * 3;
    
    // Neutral: make up the difference
    neutral = 100 - negative - positive;
    
    return {
      date,
      positive: Math.round(positive),
      neutral: Math.round(neutral),
      negative: Math.round(negative)
    };
  });
};

interface AnalyticsDashboardProps {
  theme?: 'light' | 'dark';
  onThemeToggle?: () => void;
  onOpenAgent?: () => void;
  onOpenLeadershipAgent?: () => void;
}

export function AnalyticsDashboard({ theme = 'light', onThemeToggle, onOpenAgent, onOpenLeadershipAgent }: AnalyticsDashboardProps = {}) {
  const [sentimentData] = useState(generateSentimentData());
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [showCMMCExplanation, setShowCMMCExplanation] = useState(false);
  const [showClingerCohenExplanation, setShowClingerCohenExplanation] = useState(false);
  const [showWCAGExplanation, setShowWCAGExplanation] = useState(false);
  const [showSemanticExplanation, setShowSemanticExplanation] = useState(false);
  const [showSecurityExplanation, setShowSecurityExplanation] = useState(false);
  const [showComplianceExplanation, setShowComplianceExplanation] = useState(false);
  const [showTransparencyExplanation, setShowTransparencyExplanation] = useState(false);
  
  // Authentication states
  const [showDashboard, setShowDashboard] = useState(true);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authenticatedEmail, setAuthenticatedEmail] = useState<string | null>(null);
  const { navigate } = useNavigation();

  // Check if user email is whitelisted
  const isEmailWhitelisted = (email: string | null): boolean => {
    if (!email) return false;
    return (
      email === 'carapaulson1@gmail.com' ||
      email === 'cara@oratf.info' ||
      email.endsWith('@oratf.info')
    );
  };

  // Handle Google OAuth callback (simulated for demo - integrate with real Google OAuth)
  const handleGoogleAuth = async (email: string) => {
    setIsAuthenticating(true);
    setAuthError(null);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (isEmailWhitelisted(email)) {
      // Success - store credentials and grant access
      localStorage.setItem('userEmail', email);
      localStorage.setItem('authToken', `token_${Date.now()}`);
      localStorage.setItem('authTimestamp', Date.now().toString());
      setAuthenticatedEmail(email);
      setRequiresAuth(false);
      setShowDashboard(true);
      setIsAuthenticating(false);
    } else {
      // Failed - show error
      setAuthError(`Access denied. The email "${email}" is not authorized. Please use a whitelisted account.`);
      setIsAuthenticating(false);
    }
  };

  // Handle dashboard access - show for 3 seconds then require auth
  useEffect(() => {
    if (!showDashboard) return;

    const timer = setTimeout(() => {
      // Check if user has valid auth
      const userEmail = localStorage.getItem('userEmail');
      if (!isEmailWhitelisted(userEmail)) {
        setShowDashboard(false);
        setRequiresAuth(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [showDashboard]);

  // Calculate sentiment totals for summary cards
  const totalPositive = sentimentData.reduce((sum, d) => sum + d.positive, 0);
  const totalNeutral = sentimentData.reduce((sum, d) => sum + d.neutral, 0);
  const totalNegative = sentimentData.reduce((sum, d) => sum + d.negative, 0);
  const totalAll = totalPositive + totalNeutral + totalNegative;
  
  const positivePercent = Math.ceil((totalPositive / totalAll) * 100);
  const neutralPercent = Math.ceil((totalNeutral / totalAll) * 100);
  const negativePercent = Math.ceil((totalNegative / totalAll) * 100);

  // Video Persona Analytics Data
  const videoPersonaData = [
    { name: 'Leadership Agent Video 1', views: 609, totalDuration: '14h 13m', avgDuration: '1m 24s', thumbsUp: 487, thumbsDown: 122 },
    { name: 'Workforce Agent Video 1', views: 510, totalDuration: '5h 1m', avgDuration: '35s', thumbsUp: 423, thumbsDown: 87 },
    { name: 'Leadership Agent Video 2', views: 360, totalDuration: '13h 52m', avgDuration: '2m 18s', thumbsUp: 312, thumbsDown: 48 },
    { name: 'Custom Persona Video 1', views: 205, totalDuration: '4h 37m', avgDuration: '1m 21s', thumbsUp: 178, thumbsDown: 27 },
    { name: 'Workforce Agent Video 2', views: 202, totalDuration: '8h 11m', avgDuration: '2m 25s', thumbsUp: 165, thumbsDown: 37 },
    { name: 'Custom Persona Video 2', views: 106, totalDuration: '6h 4m', avgDuration: '3m 26s', thumbsUp: 89, thumbsDown: 17 },
  ];

  // Configured Context Usage Data
  const contextUsageData = [
    { category: 'Library', value: 14, color: '#3b82f6' },
    { category: 'Video', value: 24, color: '#a855f7' },
    { category: 'News', value: 21, color: '#10b981' },
    { category: 'AI Trends', value: 8, color: '#f59e0b' },
  ];

  // Organization Profile (100 employees - small to mid-size)
  const totalEmployees = 100;
  
  // Department Breakdown Pie Chart Data
  const departmentData = [
    { name: 'Operations', value: 28, percentage: 28, color: '#3b82f6' },
    { name: 'Sales & Marketing', value: 20, percentage: 20, color: '#10b981' },
    { name: 'IT & Engineering', value: 18, percentage: 18, color: '#a855f7' },
    { name: 'Finance & Admin', value: 15, percentage: 15, color: '#f59e0b' },
    { name: 'HR & Training', value: 11, percentage: 11, color: '#ef4444' },
    { name: 'Leadership', value: 8, percentage: 8, color: '#8b5cf6' },
  ];

  // Training Completion by Department (90-day period)
  const trainingByDepartment = [
    { name: 'Leadership', completed: 8, total: 8, percentage: 100, color: '#8b5cf6' },
    { name: 'IT & Engineering', completed: 15, total: 18, percentage: 83, color: '#a855f7' },
    { name: 'HR & Training', completed: 9, total: 11, percentage: 82, color: '#ef4444' },
    { name: 'Finance & Admin', completed: 12, total: 15, percentage: 80, color: '#f59e0b' },
    { name: 'Sales & Marketing', completed: 15, total: 20, percentage: 75, color: '#10b981' },
    { name: 'Operations', completed: 20, total: 28, percentage: 71, color: '#3b82f6' },
  ];

  // Topic Engagement Distribution (90 days - 973 total interactions)
  const topicEngagementData = [
    { name: 'AI Ethics & Governance', value: 245, percentage: 25.2, color: '#8b5cf6' },
    { name: 'Prompt Engineering', value: 198, percentage: 20.3, color: '#3b82f6' },
    { name: 'Data Privacy & Security', value: 176, percentage: 18.1, color: '#ef4444' },
    { name: 'AI Tools & Applications', value: 152, percentage: 15.6, color: '#10b981' },
    { name: 'Change Management', value: 118, percentage: 12.1, color: '#f59e0b' },
    { name: 'ROI & Metrics', value: 84, percentage: 8.6, color: '#06b6d4' },
  ];

  // Agent Usage by Department (367 total video views + 2 active agents)
  const agentUsageData = [
    { name: 'IT & Engineering', usage: 94, percentage: 25.6, color: '#a855f7' },
    { name: 'Leadership', usage: 78, percentage: 21.3, color: '#8b5cf6' },
    { name: 'Sales & Marketing', usage: 69, percentage: 18.8, color: '#10b981' },
    { name: 'Operations', usage: 62, percentage: 16.9, color: '#3b82f6' },
    { name: 'Finance & Admin', usage: 38, percentage: 10.4, color: '#f59e0b' },
    { name: 'HR & Training', usage: 26, percentage: 7.1, color: '#ef4444' },
  ];

  // Issue Types Breakdown (2 issues + 1 critical incident reported)
  const issueTypesData = [
    { name: 'Access Permissions', value: 1, percentage: 33.3, color: '#ef4444' },
    { name: 'Content Quality', value: 1, percentage: 33.3, color: '#f59e0b' },
    { name: 'Technical Bugs', value: 1, percentage: 33.3, color: '#3b82f6' },
  ];

  // 90-Day AI Readiness Progression (showing improvement from 48% to 87%)
  const readinessProgressionData = [
    { week: 'Week 1', score: 48, date: 'Oct 16' },
    { week: 'Week 2', score: 52, date: 'Oct 23' },
    { week: 'Week 3', score: 55, date: 'Oct 30' },
    { week: 'Week 4', score: 58, date: 'Nov 6' },
    { week: 'Week 5', score: 61, date: 'Nov 13' },
    { week: 'Week 6', score: 64, date: 'Nov 20' },
    { week: 'Week 7', score: 67, date: 'Nov 27' },
    { week: 'Week 8', score: 70, date: 'Dec 4' },
    { week: 'Week 9', score: 73, date: 'Dec 11' },
    { week: 'Week 10', score: 76, date: 'Dec 18' },
    { week: 'Week 11', score: 80, date: 'Dec 25' },
    { week: 'Week 12', score: 83, date: 'Jan 1' },
    { week: 'Week 13', score: 87, date: 'Jan 8' },
  ];

  // Training Engagement Over 90 Days (modules completed)
  const trainingEngagementData = [
    { week: 'Week 1-2', modules: 10, employees: 15 },
    { week: 'Week 3-4', modules: 23, employees: 28 },
    { week: 'Week 5-6', modules: 38, employees: 43 },
    { week: 'Week 7-8', modules: 52, employees: 56 },
    { week: 'Week 9-10', modules: 65, employees: 67 },
    { week: 'Week 11-12', modules: 74, employees: 76 },
    { week: 'Week 13', modules: 79, employees: 79 },
  ];

  // SOP Categories Distribution (93 total SOPs)
  const sopCategoriesData = [
    { name: 'AI Usage Guidelines', value: 18, percentage: 19.4, color: '#8b5cf6' },
    { name: 'Data Handling', value: 16, percentage: 17.2, color: '#3b82f6' },
    { name: 'Security Protocols', value: 15, percentage: 16.1, color: '#ef4444' },
    { name: 'Compliance Procedures', value: 14, percentage: 15.1, color: '#f59e0b' },
    { name: 'Quality Assurance', value: 12, percentage: 12.9, color: '#10b981' },
    { name: 'Incident and Emergency Response', value: 10, percentage: 10.8, color: '#06b6d4' },
    { name: 'Other Procedures', value: 8, percentage: 8.6, color: '#64748b' },
  ];

  // Credentials & Certifications by Type (15 total)
  const credentialsData = [
    { name: 'AI/ML Certifications', value: 5, percentage: 33.3, color: '#8b5cf6' },
    { name: 'Security Certifications', value: 4, percentage: 26.7, color: '#ef4444' },
    { name: 'Cloud Certifications', value: 3, percentage: 20, color: '#3b82f6' },
    { name: 'Project Management', value: 2, percentage: 13.3, color: '#10b981' },
    { name: 'Data Analytics', value: 1, percentage: 6.7, color: '#f59e0b' },
  ];

  const exportData = () => {
    const csvContent = [
      'ORA Analytics Export - 90-Day Performance Report',
      'Generated: ' + new Date().toLocaleDateString(),
      'Organization Size: ' + totalEmployees + ' employees',
      '',
      'Cultural Climate/Sentiment Trends',
      'Date,Positive,Neutral,Negative',
      ...sentimentData.map(d => `${d.date},${d.positive},${d.neutral},${d.negative}`),
      '',
      'Video Persona Analytics',
      'Video Name,Views,Total Duration,Avg Duration,Thumbs Up,Thumbs Down',
      ...videoPersonaData.map(v => `${v.name},${v.views},${v.totalDuration},${v.avgDuration},${v.thumbsUp},${v.thumbsDown}`),
      '',
      'Department Breakdown',
      'Department,Employees,Percentage',
      ...departmentData.map(d => `${d.name},${d.value},${d.percentage}%`),
      '',
      'Training Completion by Department',
      'Department,Completed,Total,Percentage',
      ...trainingByDepartment.map(d => `${d.name},${d.completed},${d.total},${d.percentage}%`),
      '',
      'Topic Engagement Distribution (973 total)',
      'Topic,Interactions,Percentage',
      ...topicEngagementData.map(t => `${t.name},${t.value},${t.percentage}%`),
      '',
      'Agent Usage by Department (367 total)',
      'Department,Usage,Percentage',
      ...agentUsageData.map(a => `${a.name},${a.usage},${a.percentage}%`),
      '',
      'SOP Categories (93 total)',
      'Category,Count,Percentage',
      ...sopCategoriesData.map(s => `${s.name},${s.value},${s.percentage}%`),
      '',
      'Credentials & Certifications (15 total)',
      'Type,Count,Percentage',
      ...credentialsData.map(c => `${c.name},${c.value},${c.percentage}%`),
      '',
      '90-Day AI Readiness Progression',
      'Week,Score,Date',
      ...readinessProgressionData.map(r => `${r.week},${r.score},${r.date}`),
      '',
      'Training Engagement Progress',
      'Period,Modules Completed,Employees Engaged',
      ...trainingEngagementData.map(t => `${t.week},${t.modules},${t.employees}`),
      '',
      'AI Leadership Training (90 days)',
      'Courses Completed,3',
      'Modules Completed,7/10',
      'Q&A Sessions Attended,2/3',
      'Module Attempts,28',
      'Module Completion Rate,70%',
      '',
      'Summary Metrics',
      'AI Governance Maturity Index (AGMI),89%',
      'Total Employees,100',
      'CMMC Compliance,94%',
      'RBAC Audits,100%',
      'Semantic Context,94%',
      'AI Leadership & Governance Readiness,87%',
      'Transparency & Accessibility Score,7/10',
      'Secure Data Scheme,9/9',
      'Issues Reported,2',
      'Critical Incidents,1',
      'Average Incident Response time,24 hr',
      'Video Views,367',
      'Configured Contexts,101',
      'SOPs Documented,93',
      'AI Leadership Training,40%',
      'RACI Completion,0%',
      'Credentials & Certifications,15',
      'Training Completion Rate,79% (79/100 employees)',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ora-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen w-full bg-gray-100">
      {/* Top Navigation Bar */}
      <div className="w-full bg-white border-b border-gray-200 shadow-sm">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          {/* Left: ORA Home Button */}
          <button
            onClick={() => navigate('/unified-agent')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-all"
            aria-label="Go to ORA Home"
          >
            <Home className="size-5 text-black" />
            <span className="font-semibold text-lg text-black">ORA</span>
          </button>

          {/* Right: Agent Icons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenLeadershipAgent ? onOpenLeadershipAgent() : navigate('/unified-agent')}
              className="flex items-center justify-center p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-all"
              aria-label="Open Leadership Agent"
              title="Leadership Agent"
            >
              <Brain className="size-7 fill-yellow-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto p-3 sm:p-4 lg:p-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Agentic AI Analytics Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
              Real-time governance and performance metrics
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap items-center justify-end w-full sm:w-auto">
            <button
              onClick={exportData}
              className="px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-all border border-purple-700 flex items-center gap-1.5 sm:gap-2"
            >
              <Download className="size-3 sm:size-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>
        </div>

        {/* AI Governance Maturity Index - Combined Metric */}
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-300 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-black">AI Governance Maturity Index</h2>
              <p className="text-sm text-gray-600">Composite score across 6 key governance metrics</p>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-black mb-1">89%</div>
              <div className="text-xs text-green-600">
                +12pts (90 days)
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-center border-2 border-yellow-400 rounded-lg p-2 bg-yellow-50 relative">
              <p className="text-xs text-gray-700 mb-1">Leadership</p>
              <p className="text-lg font-bold text-black">87%</p>
              <p className="text-[9px] text-gray-500 mb-1">25% weight</p>
              <div className="flex justify-center">
                <Award className="size-5 text-yellow-600 fill-yellow-600" />
              </div>
            </div>
            <div 
              className="text-center cursor-pointer hover:bg-gray-100 rounded-lg p-2 transition-colors"
              onClick={() => setShowTransparencyExplanation(true)}
              title="Click to learn more about Transparency"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-xs text-gray-700">Transparency</p>
                <Info className="size-3 text-blue-600" />
              </div>
              <p className="text-lg font-bold text-black">70%</p>
              <p className="text-[9px] text-gray-500">15% weight</p>
            </div>
            <div 
              className="text-center cursor-pointer hover:bg-gray-100 rounded-lg p-2 transition-colors"
              onClick={() => setShowSecurityExplanation(true)}
              title="Click to learn more about Security"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-xs text-gray-700">Security</p>
                <Info className="size-3 text-blue-600" />
              </div>
              <p className="text-lg font-bold text-black">100%</p>
              <p className="text-[9px] text-gray-500">10% weight</p>
            </div>
            <div 
              className="text-center rounded-lg p-2 bg-white cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setShowComplianceExplanation(true)}
              title="Click to learn more about Credentials & Certifications"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-xs text-gray-700 font-semibold">Compliance</p>
                <Info className="size-3 text-blue-600" />
              </div>
              <p className="text-lg font-bold text-black">97%</p>
              <p className="text-[9px] text-gray-500">20% weight</p>
            </div>
            <div 
              className="text-center cursor-pointer hover:bg-gray-100 rounded-lg p-2 transition-colors"
              onClick={() => setShowSemanticExplanation(true)}
              title="Click to learn more about Semantic Context"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <p className="text-xs text-gray-700">Semantic</p>
                <Info className="size-3 text-blue-600" />
              </div>
              <p className="text-lg font-bold text-black">94%</p>
              <p className="text-[9px] text-gray-500">10% weight</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-700 mb-1">Incident Response</p>
              <p className="text-lg font-bold text-black">92%</p>
              <p className="text-[9px] text-gray-500">10% weight</p>
            </div>
          </div>

          {/* Risk Assessment Section */}
          <div className="mt-4 bg-white border-2 border-gray-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-black">Risk Assessment</h3>
                <p className="text-[10px] text-gray-600">Financial, Bias, Vendor Lock-in, and Other Factors</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-black">85%</p>
                <p className="text-[9px] text-gray-500">10% weight</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-600">Financial Risk</p>
                <p className="font-bold text-black">88%</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-600">Bias Management</p>
                <p className="font-bold text-black">82%</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-600">Vendor Lock-in</p>
                <p className="font-bold text-black">80%</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-600">Model Drift</p>
                <p className="font-bold text-black">90%</p>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-start gap-2 text-xs text-black">
              <Info className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">How AGMI is Calculated:</p>
                <p className="leading-relaxed text-gray-700">
                  Weighted average of 6 governance metrics: Compliance - combines CMMC & RBAC (20%), 
                  Semantic Context (10%), AI Leadership & Governance Readiness (25%), Transparency & 
                  Accessibility (15%), Secure Data Scheme (10%), Incident Response (10%), and Risk Assessment (10%). Higher weights reflect strategic importance.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-gray-700">
            <span className="font-semibold text-green-600">✨ You're in the top 10%</span>
          </div>
        </div>

        {/* 90-Day Executive Summary */}
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-300 mb-6">
          <div className="mb-6 pb-4 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-black">90-Day Executive Summary</h2>
            <p className="text-sm text-gray-600">Organization-wide AI adoption metrics</p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">Total Employees</p>
              <p className="text-3xl font-bold text-black">{totalEmployees}</p>
              <p className="text-xs text-gray-600 mt-1">6 departments</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">AI Training Complete</p>
              <p className="text-3xl font-bold text-black">79%</p>
              <p className="text-xs text-gray-600 mt-1">79 of 100</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">AI Readiness</p>
              <p className="text-3xl font-bold text-black">87%</p>
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <TrendingUp className="size-3" />
                +39pts growth
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">Topic Engagements</p>
              <p className="text-3xl font-bold text-black">973</p>
              <p className="text-xs text-gray-600 mt-1">6 categories</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">Agent Interactions</p>
              <p className="text-3xl font-bold text-black">367</p>
              <p className="text-xs text-gray-600 mt-1">video views</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-700 font-semibold uppercase mb-1">SOPs Created</p>
              <p className="text-3xl font-bold text-black">93</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="size-3" />
                  WCAG compliant
                </p>
                <button
                  onClick={() => setShowWCAGExplanation(true)}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is WCAG?"
                >
                  <Info className="size-3" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Info className="size-4 text-gray-700" />
              <p className="text-sm font-semibold text-gray-700">Key Highlights</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs text-gray-600">
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">100% Leadership training completion</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">83% IT & Engineering adoption rate</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">25% engagement in AI Ethics topics</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">15 professional certifications earned</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">94% semantic context understanding</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">Only 2 issues + 1 incident (99.7% success)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cultural Climate/Sentiment Trends Over Time */}
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-md border border-gray-200">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <div className="bg-gradient-to-br from-green-400 to-emerald-600 p-2 rounded-lg">
                <TrendingUp className="size-5 text-white" />
              </div>
              Cultural Climate/Sentiment Trends Over Time
            </h2>
            <p className="text-sm text-gray-500 ml-11 mt-1">(In relation to AI adoption)</p>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sentimentData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#4b5563" style={{ fontSize: '12px' }} />
              <YAxis domain={[0, 100]} label={{ value: 'Sentiment Score', angle: -90, position: 'insideLeft', style: { fill: '#1f2937' } }} stroke="#4b5563" style={{ fontSize: '12px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: '#1f2937' }} />
              <Line type="monotone" dataKey="positive" stroke="#10b981" strokeWidth={2} name="Positive" dot={{ fill: '#10b981' }} />
              <Line type="monotone" dataKey="neutral" stroke="#f59e0b" strokeWidth={2} name="Neutral" dot={{ fill: '#f59e0b' }} />
              <Line type="monotone" dataKey="negative" stroke="#ef4444" strokeWidth={2} name="Negative" dot={{ fill: '#ef4444' }} />
            </LineChart>
          </ResponsiveContainer>

          {/* Summary Cards Below Chart */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm font-semibold text-green-700 uppercase mb-1">Positive Trend</p>
              <p className="text-2xl font-bold text-green-600 flex items-center gap-2">
                <TrendingUp className="size-6" />
                {positivePercent}%
              </p>
              <p className="text-xs text-green-600 mt-1">Significant improvement</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm font-semibold text-amber-700 uppercase mb-1">Neutral</p>
              <p className="text-2xl font-bold text-amber-600 flex items-center gap-2">
                <ArrowRight className="size-6" />
                {neutralPercent}%
              </p>
              <p className="text-xs text-amber-600 mt-1">Stable foundation</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-semibold text-red-700 uppercase mb-1">Negative Trend</p>
              <p className="text-2xl font-bold text-red-600 flex items-center gap-2">
                <ChevronDown className="size-6" />
                {negativePercent}%
              </p>
              <p className="text-xs text-red-600 mt-1">Significant improvement</p>
            </div>
          </div>
        </div>

        {/* Main Metrics Grid - 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Row 1 */}
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative cursor-pointer hover:shadow-lg transition-shadow">
            <div className="absolute top-4 right-4">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Shield className="size-5 text-purple-600" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">RBAC Audits</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Role-Based Access Control quarterly compliance and persistent monitoring</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">100%</p>
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="size-3" />
                Compliant
              </p>
              <p className="text-[10px] text-purple-600 mt-1">+ 1 Air-gapped segment (privacy/security)</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Brain className="size-5 text-blue-600 fill-yellow-400" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Semantic Context</p>
              <p className="text-[10px] text-gray-500 mt-0.5">AI context understanding and comprehension quality</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">94%</p>
              <p className="text-xs text-blue-600 mt-1">+ 2% this week</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <Users className="size-5 text-green-600" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">AI Leadership & Governance Readiness</p>
              <p className="text-[10px] text-gray-500 mt-0.5">ML-predicted readiness score for AI adoption</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">87%</p>
              <p className="text-xs text-green-600 mt-1">⚡ Strong readiness</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Shield className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Transparency & Accessibility Score</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Measures how transparent and accessible your AI Governance and supporting documentation is for stakeholders</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">7/10</p>
            </div>
          </div>

          {/* Row 2 */}
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-emerald-600 p-2 rounded-lg">
                <Lock className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Secure Data Scheme</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Tracks 9 security components automatically monitored by Supabase for data protection</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">9/9</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="size-3" />
                  WCAG Compliant
                </p>
                <button
                  onClick={() => setShowWCAGExplanation(true)}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is WCAG?"
                >
                  <Info className="size-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-orange-600 p-2 rounded-lg">
                <PlayCircle className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Integrated Workforce Videos</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Number of workforce transition and training videos successfully integrated into the platform</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">15</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-yellow-500 p-2 rounded-lg">
                <AlertCircle className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Issues/Incidents Reported</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Critical issues identified</p>
              <div className="mt-2 space-y-0.5">
                <p className="text-sm font-semibold text-gray-900">2 Issues</p>
                <p className="text-sm font-semibold text-red-600">1 Critical incident</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-indigo-500 p-2 rounded-lg">
                <Clock className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Average Incident Response time</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Time to resolution</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">24 hr</p>
            </div>
          </div>
        </div>

        {/* Secondary Metrics Grid - Expandable Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Row 3 */}
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-purple-500 p-2 rounded-lg">
                <Video className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Video Views</p>
              <p className="text-[10px] text-gray-500 mt-0.5">307 views across training content</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">367</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-teal-500 p-2 rounded-lg">
                <Bot className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Automated Agentic AI Connections</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Active AI agent connections</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">2</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-indigo-500 p-2 rounded-lg">
                <Settings className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Configured Contexts</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Context configurations active</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">101</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative">
            <div className="absolute top-4 right-4">
              <div className="bg-green-600 p-2 rounded-lg">
                <DollarSign className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Operational Modernization & Maintenance Budget</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Tracks budget allocation and spending for AI modernization initiatives</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">$0</p>
            </div>
          </div>

          {/* Row 4 */}
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative cursor-pointer hover:shadow-lg transition-shadow"
               onClick={() => setExpandedPanel(expandedPanel === 'sops' ? null : 'sops')}>
            <div className="absolute top-4 right-4">
              <div className="bg-cyan-600 p-2 rounded-lg">
                <FileText className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">SOPs Documented Automated & Accessible</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Standard operating procedures & automation</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">93</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="size-3" />
                  WCAG Compliant
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWCAGExplanation(true);
                  }}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is WCAG?"
                >
                  <Info className="size-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative cursor-pointer hover:shadow-lg transition-shadow"
               onClick={() => setExpandedPanel(expandedPanel === 'training' ? null : 'training')}>
            <div className="absolute top-4 right-4">
              <div className="bg-violet-600 p-2 rounded-lg">
                <GraduationCap className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">AI Leadership Training</p>
              <p className="text-[10px] text-gray-500 mt-0.5">7/10 Modules, 2/3 Q&A Sessions (90 days)</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">70%</p>
              <p className="text-xs text-blue-600 mt-1">⏱ 3 Completed</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative cursor-pointer hover:shadow-lg transition-shadow"
               onClick={() => setExpandedPanel(expandedPanel === 'raci' ? null : 'raci')}>
            <div className="absolute top-4 right-4">
              <div className="bg-rose-600 p-2 rounded-lg">
                <Network className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">RACI Completion</p>
              <p className="text-[10px] text-gray-500 mt-0.5">0 Completed, 1 In Progress, 1 Pending</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0%</p>
              <p className="text-xs text-amber-600 mt-1">+ Implementation Starting</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 relative cursor-pointer hover:shadow-lg transition-shadow"
               onClick={() => setExpandedPanel(expandedPanel === 'credentials' ? null : 'credentials')}>
            <div className="absolute top-4 right-4">
              <div className="bg-orange-600 p-2 rounded-lg">
                <Award className="size-5 text-white" />
              </div>
            </div>
            <div className="pr-12">
              <p className="text-xs font-bold text-black">Credentials & Certifications</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Team certifications and credential completions for AI governance</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">15</p>
              <p className="text-xs text-green-600 mt-1">→ +3 this month</p>
            </div>
          </div>
        </div>

        {/* Expandable Detail Panels */}
        {expandedPanel === 'raci' && (
          <div className="bg-white rounded-lg p-6 shadow-lg border-2 border-rose-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <Network className="size-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">RACI Completion</h3>
                <p className="text-sm text-gray-600">Governance matrix implementation status</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                <p className="text-3xl font-bold text-green-600">0</p>
                <p className="text-sm text-gray-600 mt-1">Completed</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">1</p>
                <p className="text-sm text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-200">
                <p className="text-3xl font-bold text-amber-600">1</p>
                <p className="text-sm text-gray-600 mt-1">Pending</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 text-gray-900 rounded-full p-1.5 text-xs font-bold w-8 h-8 flex items-center justify-center">R</div>
                  <div>
                    <p className="font-semibold text-gray-900">Responsible</p>
                    <p className="text-xs text-gray-600">Does the work</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 text-gray-900 rounded-full p-1.5 text-xs font-bold w-8 h-8 flex items-center justify-center">A</div>
                  <div>
                    <p className="font-semibold text-gray-900">Accountable</p>
                    <p className="text-xs text-gray-600">Decision maker</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500 text-gray-900 rounded-full p-1.5 text-xs font-bold w-8 h-8 flex items-center justify-center">C</div>
                  <div>
                    <p className="font-semibold text-gray-900">Consulted</p>
                    <p className="text-xs text-gray-600">Provides input</p>
                  </div>
                </div>
                <Info className="size-5 text-blue-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500 text-gray-900 rounded-full p-1.5 text-xs font-bold w-8 h-8 flex items-center justify-center">I</div>
                  <div>
                    <p className="font-semibold text-gray-900">Informed</p>
                    <p className="text-xs text-gray-600">Kept in loop</p>
                  </div>
                </div>
                <Info className="size-5 text-amber-600" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 mb-4 border border-green-200">
              <p className="font-semibold text-gray-900 mb-2">💡 Next Step</p>
              <p className="text-sm text-gray-700">Create team RACI matrices with Product Owners for your AI projects. Clear role definitions prevent confusion and accelerate decision-making with clear targets and financial outcomes.</p>
            </div>

            <button className="w-full bg-green-600 text-black py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
              View RACI Template
              <ArrowRight className="size-4" />
            </button>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-1">Governance:</p>
              <p className="text-xs text-gray-600">RACI matrices are necessary to monitor and govern automated organizational processes.</p>
            </div>
          </div>
        )}

        {expandedPanel === 'training' && (
          <div className="bg-white rounded-lg p-6 shadow-lg border-2 border-violet-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-lg">
                <GraduationCap className="size-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">AI Leadership Training</h3>
                <p className="text-sm text-gray-600">90-day training program & Q&A sessions</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                <p className="text-3xl font-bold text-green-600">3</p>
                <p className="text-sm text-gray-600 mt-1">Courses Complete</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">7/10</p>
                <p className="text-sm text-gray-600 mt-1">Modules Done</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">2/3</p>
                <p className="text-sm text-gray-600 mt-1">Q&A Sessions</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Module Completion</p>
                <p className="text-sm font-bold text-blue-600">70% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '70%' }}></div>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Users className="size-5 text-gray-700" />
                    <h4 className="font-semibold text-gray-900">Leadership Accelerator Course</h4>
                  </div>
                  <CheckCircle className="size-5 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 mb-2">3 participants completed the AI Leadership Accelerator course</p>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-700">Completion Rate:</span>
                  <span className="font-semibold text-green-600">100%</span>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <BookOpen className="size-5 text-gray-700" />
                    <h4 className="font-semibold text-gray-900">Module Progress</h4>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">28 attempts</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">7 of 10 modules completed with 28 total attempts</p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                </div>
                <p className="text-xs text-blue-600 mt-1 text-right">70% Complete</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="size-5 text-gray-700" />
                    <h4 className="font-semibold text-gray-900">Q&A Session Attendance</h4>
                  </div>
                  <AlertCircle className="size-5 text-amber-600" />
                </div>
                <p className="text-sm text-gray-600 mb-2">2 of 3 monthly Q&A sessions attended (1 per month expected)</p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-amber-600 h-2 rounded-full" style={{ width: '66%' }}></div>
                </div>
                <p className="text-xs text-amber-600 mt-1 text-right">Next: Month 3 Review</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
              <p className="font-semibold text-gray-900 mb-2">📈 90-Day Progress</p>
              <p className="text-sm text-gray-700">3 participants completed the Leadership Accelerator course. Continue module progress and attend the final Q&A session to maintain certification eligibility.</p>
            </div>

            <button className="w-full bg-blue-600 text-black py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
              Workforce Training & Readiness Dashboard
              <ArrowRight className="size-4" />
            </button>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-700 mb-1">Certification Requirements:</p>
              <p className="text-xs text-blue-600">Complete all 10 modules and attend 3 monthly Q&A sessions within 90 days to earn AI Leadership Accelerator certification.</p>
            </div>
          </div>
        )}

        {expandedPanel === 'sops' && (
          <div className="bg-white rounded-lg p-6 shadow-lg border-2 border-cyan-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <FileText className="size-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">SOPs Documented</h3>
                <p className="text-sm text-gray-600">Standard operating procedures & automation</p>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">93</p>
                <p className="text-sm text-gray-600 mt-1">SOPs</p>
              </div>
              <div className="flex-1 mx-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-green-600">34</p>
                    <p className="text-xs text-gray-600 mt-1">Documented</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                    <p className="text-2xl font-bold text-blue-600">28</p>
                    <p className="text-xs text-gray-600 mt-1">Automated</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                    <p className="text-2xl font-bold text-green-600">31</p>
                    <p className="text-xs text-gray-600 mt-1">Accessible</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Completion Status</p>
                <p className="text-sm font-bold text-green-600">83% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-600 h-3 rounded-full" style={{ width: '83%' }}></div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <p className="font-semibold text-gray-900">Key SOP Categories</p>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <FileCheck className="size-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Customer Onboarding</p>
                    <p className="text-xs text-gray-600">New customer setup procedures</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <FileCheck className="size-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Data Management</p>
                    <p className="text-xs text-gray-600">Data handling & retention policies</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <FileCheck className="size-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Incident and Emergency Response</p>
                    <p className="text-xs text-gray-600">Incident response & escalation</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <FileCheck className="size-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">System Maintenance</p>
                    <p className="text-xs text-gray-600">Routine maintenance schedules</p>
                  </div>
                </div>
                <Info className="size-5 text-blue-600" />
              </div>
            </div>

            <button className="w-full bg-green-600 text-black py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 mb-4">
              View SOP Library
              <ArrowRight className="size-4" />
            </button>

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-green-700">Accessibility Status:</p>
                <button
                  onClick={() => setShowWCAGExplanation(true)}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is WCAG?"
                >
                  <Info className="size-4" />
                </button>
              </div>
              <p className="text-xs text-green-600">All SOPs are WCAG compliant and available in multiple formats.</p>
            </div>
          </div>
        )}

        {expandedPanel === 'credentials' && (
          <div className="bg-white rounded-lg p-6 shadow-lg border-2 border-orange-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-100 p-3 rounded-lg">
                <Award className="size-6 text-red-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Credentials & Certifications</h3>
                    <p className="text-sm text-gray-600">Cyber security, PII protection & compliance</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-red-600">67%</p>
                    <p className="text-xs text-gray-600">Certified</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                <p className="text-3xl font-bold text-green-600">12</p>
                <p className="text-sm text-gray-600 mt-1">Certified</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">4</p>
                <p className="text-sm text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-200">
                <p className="text-3xl font-bold text-amber-600">2</p>
                <p className="text-sm text-gray-600 mt-1">Pending</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Certification Status</p>
                <p className="text-sm font-bold text-red-600">67% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-red-600 h-3 rounded-full" style={{ width: '67%' }}></div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <p className="font-semibold text-gray-900">Active Frameworks & Standards</p>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <Shield className="size-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">SOC 2 Type II</p>
                    <p className="text-xs text-gray-600">Security & availability compliance</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <Shield className="size-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">ISO 27001</p>
                    <p className="text-xs text-gray-600">Information security management</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <Shield className="size-5 text-green-600" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">NIST Compliance/CMMC</p>
                      <button
                        onClick={() => setShowCMMCExplanation(true)}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="What is CMMC?"
                      >
                        <FileText className="size-4" />
                      </button>
                      <a
                        href="https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-167.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="NIST SP 800-167 PDF"
                      >
                        <Download className="size-4" />
                      </a>
                      <a
                        href="https://www.nist.gov/itl/ai-risk-management-framework"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="NIST AI Risk Management Framework"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    </div>
                    <p className="text-xs text-gray-600">Cyber Security & PII/PHI</p>
                  </div>
                </div>
                <CheckCircle className="size-5 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Shield className="size-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">HIPAA Ready</p>
                    <p className="text-xs text-gray-600">Healthcare data security</p>
                  </div>
                </div>
                <AlertCircle className="size-5 text-blue-600" />
              </div>
            </div>

            <button className="w-full bg-red-600 text-black py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 mb-4">
              View Compliance Details
              <ArrowRight className="size-4" />
            </button>

            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-semibold text-red-700 mb-1">Data Protection:</p>
              <p className="text-xs text-red-600">Comprehensive PII safeguards, cyber security protocols, and industry certifications ensure organizational and customer data security compliance.</p>
            </div>
          </div>
        )}

        {/* 90-Day Analytics Overview Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="size-7 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">90-Day Performance Overview</h2>
              <p className="text-sm text-gray-600">Comprehensive analytics for 100-employee organization</p>
            </div>
          </div>
        </div>

        {/* Organization & Engagement Pie Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Department Breakdown Pie Chart */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Users className="size-5 text-blue-600" />
                Organization Structure
              </h3>
              <p className="text-sm text-gray-600 mt-1">Employee distribution across {totalEmployees} total employees</p>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={departmentData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {departmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {departmentData.map((dept, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: dept.color }}></div>
                      <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{dept.value} ({dept.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Topic Engagement Pie Chart */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Tag className="size-5 text-purple-600" />
                Topic Engagement Distribution
              </h3>
              <p className="text-sm text-gray-600 mt-1">973 total topic interactions over 90 days</p>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={topicEngagementData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {topicEngagementData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {topicEngagementData.map((topic, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: topic.color }}></div>
                      <span className="text-xs font-medium text-gray-900">{topic.name}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-700">{topic.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Training Completion by Department */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <GraduationCap className="size-5 text-green-600" />
                Training Completion by Department
              </h3>
              <p className="text-sm text-gray-600 mt-1">79 of 100 employees completed training (79%)</p>
            </div>
            <div className="space-y-3">
              {trainingByDepartment.map((dept, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: dept.color }}></div>
                      <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: dept.color }}>
                      {dept.completed}/{dept.total} ({dept.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${dept.percentage}%`, backgroundColor: dept.color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Agent Usage by Department */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Bot className="size-5 text-teal-600" />
                AI Agent Usage by Department
              </h3>
              <p className="text-sm text-gray-600 mt-1">367 total agent interactions across departments</p>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={agentUsageData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="usage"
                    >
                      {agentUsageData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {agentUsageData.map((dept, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: dept.color }}></div>
                      <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{dept.usage} ({dept.percentage.toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SOP Categories Distribution */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="size-5 text-cyan-600" />
                SOP Categories Distribution
              </h3>
              <p className="text-sm text-gray-600 mt-1">93 standard operating procedures documented</p>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={sopCategoriesData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sopCategoriesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {sopCategoriesData.map((category, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                      <span className="text-xs font-medium text-gray-900">{category.name}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-700">{category.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Credentials & Certifications */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Award className="size-5 text-amber-600" />
                Credentials & Certifications
              </h3>
              <p className="text-sm text-gray-600 mt-1">15 professional certifications earned</p>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={credentialsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {credentialsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-2">
                {credentialsData.map((cert, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full" style={{ backgroundColor: cert.color }}></div>
                      <span className="text-sm font-medium text-gray-900">{cert.name}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{cert.value} ({cert.percentage.toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 90-Day Progression Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* AI Readiness Progression */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="size-5 text-green-600" />
                90-Day AI Readiness Progression
              </h3>
              <p className="text-sm text-gray-600 mt-1">Growth from 48% to 87% (+39 points)</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={readinessProgressionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" stroke="#4b5563" style={{ fontSize: '11px' }} />
                <YAxis domain={[0, 100]} stroke="#4b5563" style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#1f2937' }} />
                <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={3} name="Readiness Score" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Training Engagement Progression */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <GraduationCap className="size-5 text-purple-600" />
                Training Engagement Over 90 Days
              </h3>
              <p className="text-sm text-gray-600 mt-1">Cumulative modules completed and employee participation</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trainingEngagementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" stroke="#4b5563" style={{ fontSize: '11px' }} />
                <YAxis stroke="#4b5563" style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#1f2937' }} />
                <Bar dataKey="modules" fill="#a855f7" name="Modules Completed" />
                <Bar dataKey="employees" fill="#3b82f6" name="Employees Engaged" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Three Permanent Detail Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* RACI Completion Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <Network className="size-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">RACI Completion</h3>
                <p className="text-xs text-gray-600">Governance matrix implementation status</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">0</p>
                <p className="text-xs text-gray-600 mt-1">Completed</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-blue-600">1</p>
                <p className="text-xs text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-amber-600">1</p>
                <p className="text-xs text-gray-600 mt-1">Pending</p>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 text-gray-900 rounded-full text-xs font-bold w-6 h-6 flex items-center justify-center">R</div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Responsible</p>
                    <p className="text-xs text-gray-600">Does the work</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 text-gray-900 rounded-full text-xs font-bold w-6 h-6 flex items-center justify-center">A</div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Accountable</p>
                    <p className="text-xs text-gray-600">Decision maker</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500 text-gray-900 rounded-full text-xs font-bold w-6 h-6 flex items-center justify-center">C</div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Consulted</p>
                    <p className="text-xs text-gray-600">Provides input</p>
                  </div>
                </div>
                <Info className="size-4 text-blue-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-500 text-gray-900 rounded-full text-xs font-bold w-6 h-6 flex items-center justify-center">I</div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Informed</p>
                    <p className="text-xs text-gray-600">Kept in loop</p>
                  </div>
                </div>
                <Info className="size-4 text-amber-600" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-3 mb-3 border border-green-200">
              <p className="text-sm font-semibold text-gray-900 mb-1">💡 Next Step</p>
              <p className="text-xs text-gray-700">Create team RACI matrices with Product Owners for your AI projects. Clear role definitions prevent confusion and accelerate decision-making.</p>
            </div>

            <button className="w-full bg-green-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
              View RACI Template
              <ArrowRight className="size-4" />
            </button>

            <div className="mt-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-semibold text-gray-700 mb-0.5">Governance:</p>
              <p className="text-xs text-gray-600">RACI matrices are necessary to monitor and govern automated organizational processes.</p>
            </div>
          </div>

          {/* AI Leadership Training Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <GraduationCap className="size-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">AI Leadership Training</h3>
                  <p className="text-xs text-gray-600">90-day training program & Q&A sessions</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-600">7</p>
                <p className="text-xs text-gray-600">Modules</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">3</p>
                <p className="text-xs text-gray-600 mt-1">Courses Complete</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-blue-600">7/10</p>
                <p className="text-xs text-gray-600 mt-1">Modules Done</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-blue-600">2/3</p>
                <p className="text-xs text-gray-600 mt-1">Q&A Sessions</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Module Completion</p>
                <p className="text-sm font-bold text-blue-600">70% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '70%' }}></div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm font-semibold text-gray-900">Participant Progress (90 days)</p>
              
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Users className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Leadership Accelerator</p>
                    <p className="text-xs text-gray-600">3 participants completed course</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <BookOpen className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Module Attempts</p>
                    <p className="text-xs text-gray-600">28 total module attempts</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-blue-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <MessageSquare className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Q&A Attendance</p>
                    <p className="text-xs text-gray-600">2 of 3 sessions attended</p>
                  </div>
                </div>
                <AlertCircle className="size-4 text-amber-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Award className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Next Q&A Session</p>
                    <p className="text-xs text-gray-600">Scheduled for Month 3 review</p>
                  </div>
                </div>
                <Info className="size-4 text-blue-600" />
              </div>
            </div>

            <button className="w-full bg-blue-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mb-3">
              Workforce Training & Readiness Dashboard
              <ArrowRight className="size-4" />
            </button>

            <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 mb-0.5">90-Day Program:</p>
              <p className="text-xs text-blue-600">Complete 10 modules and attend 3 monthly Q&A sessions (1 per month) to earn AI Leadership Accelerator certification.</p>
            </div>
          </div>

          {/* RBAC Audits Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Shield className="size-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">RBAC Audits</h3>
                    <p className="text-xs text-gray-600">Role-Based Access Control quarterly compliance and persistent monitoring</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-purple-600">75%</p>
                    <p className="text-xs text-gray-600">Compliance</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">18</p>
                <p className="text-xs text-gray-600 mt-1">Verified</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-blue-600">4</p>
                <p className="text-xs text-gray-600 mt-1">In Review</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-amber-600">2</p>
                <p className="text-xs text-gray-600 mt-1">Pending</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Audit Progress</p>
                <p className="text-sm font-bold text-purple-600">75% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-purple-600 h-3 rounded-full" style={{ width: '75%' }}></div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm font-semibold text-gray-900">Role Access Control</p>
              
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Lock className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Admin</p>
                    <p className="text-xs text-gray-600">Full access & threshold setting</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-purple-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Lock className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Editor</p>
                    <p className="text-xs text-gray-600">Can propose & edit updates</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-purple-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Lock className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Viewer</p>
                    <p className="text-xs text-gray-600">Read-only access to metrics</p>
                  </div>
                </div>
                <Info className="size-4 text-blue-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Lock className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Collaborator</p>
                    <p className="text-xs text-gray-600">Limited edit permissions</p>
                  </div>
                </div>
                <Info className="size-4 text-amber-600" />
              </div>
            </div>

            <button className="w-full bg-purple-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 mb-3">
              Review RBAC Configuration
              <ArrowRight className="size-4" />
            </button>

            <div className="p-2 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs font-semibold text-purple-700 mb-0.5">NIST Standard:</p>
              <p className="text-xs text-purple-600">RBAC ensures that Admins set thresholds, Editors propose updates, and Viewers monitor metrics securely.</p>
            </div>
          </div>
        </div>

        {/* Three Additional Detail Cards - Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* SOPs Documented Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-3 rounded-lg">
                  <FileText className="size-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">SOPs Documented</h3>
                  <p className="text-xs text-gray-600">Standard operating procedures & automation</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-green-600">93</p>
                <p className="text-xs text-gray-600">SOPs</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">34</p>
                <p className="text-xs text-gray-600 mt-1">Documented</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">28</p>
                <p className="text-xs text-gray-600 mt-1">Automated</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">31</p>
                <p className="text-xs text-gray-600 mt-1">Accessible</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Completion Status</p>
                <p className="text-sm font-bold text-green-600">83% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-600 h-3 rounded-full" style={{ width: '83%' }}></div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm font-semibold text-gray-900">Key SOP Categories</p>
              
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Customer Onboarding</p>
                    <p className="text-xs text-gray-600">New customer setup procedures</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Data Management</p>
                    <p className="text-xs text-gray-600">Data handling & retention policies</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Emergency Response</p>
                    <p className="text-xs text-gray-600">Incident response & escalation</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">System Maintenance</p>
                    <p className="text-xs text-gray-600">Routine maintenance schedules</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>
            </div>

            <button className="w-full bg-green-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 mb-3">
              View SOP Library
              <ArrowRight className="size-4" />
            </button>

            <div className="p-2 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-0.5">
                <p className="text-xs font-semibold text-green-700">Accessibility Status:</p>
                <button
                  onClick={() => setShowWCAGExplanation(true)}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is WCAG?"
                >
                  <Info className="size-4" />
                </button>
              </div>
              <p className="text-xs text-green-600">All SOPs are WCAG compliant and available in multiple formats.</p>
            </div>
          </div>

          {/* Modernization Budget Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Zap className="size-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Modernization Budget</h3>
                  <p className="text-xs text-gray-600">Upskilling: Operational efficiency & infrastructure investments</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-600">77%</p>
                <p className="text-xs text-gray-600">Allocated</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                <p className="text-lg font-bold text-blue-600">$185K</p>
                <p className="text-xs text-gray-600 mt-1">Total Budget</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                <p className="text-lg font-bold text-amber-600">$142K</p>
                <p className="text-xs text-gray-600 mt-1">Spent</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                <p className="text-lg font-bold text-green-600">$43K</p>
                <p className="text-xs text-gray-600 mt-1">Remaining</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Budget Utilization</p>
                <p className="text-sm font-bold text-blue-600">77% Used</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '77%' }}></div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm font-semibold text-gray-900">Active Projects</p>
              
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Cloud className="size-4 text-gray-700" />
                    <h4 className="text-sm font-semibold text-gray-900">Cloud Infrastructure</h4>
                  </div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">In Progress</span>
                </div>
                <p className="text-xs text-gray-600">Migration to cloud-native architecture</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Package className="size-4 text-gray-700" />
                    <h4 className="text-sm font-semibold text-gray-900">System Integration</h4>
                  </div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">In Progress</span>
                </div>
                <p className="text-xs text-gray-600">API & third-party integrations</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="size-4 text-gray-700" />
                    <h4 className="text-sm font-semibold text-gray-900">Specialized Talent</h4>
                  </div>
                  <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-1 rounded">Planned</span>
                </div>
                <p className="text-xs text-gray-600">Hire or contract AI Engineers & Agile Professional Developers</p>
              </div>
            </div>

            <button className="w-full bg-blue-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mb-3">
              View Budget Details
              <ArrowRight className="size-4" />
            </button>

            <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-700 mb-0.5">Transparency & Accountability:</p>
                  <p className="text-xs text-blue-600">Clinger-Cohen Act compliance ensures budget transparency.</p>
                </div>
                <button
                  onClick={() => setShowClingerCohenExplanation(true)}
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                  title="What is the Clinger-Cohen Act?"
                >
                  <FileText className="size-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Credentials & Certifications Card */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-3 rounded-lg">
                  <Shield className="size-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Credentials & Certifications</h3>
                  <p className="text-xs text-gray-600">Cyber security, PII protection & compliance</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-red-600">67%</p>
                <p className="text-xs text-gray-600">Certified</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-green-600">12</p>
                <p className="text-xs text-gray-600 mt-1">Certified</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-blue-600">4</p>
                <p className="text-xs text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-2xl font-bold text-amber-600">2</p>
                <p className="text-xs text-gray-600 mt-1">Pending</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-700">Certification Status</p>
                <p className="text-sm font-bold text-red-600">67% Complete</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-red-600 h-3 rounded-full" style={{ width: '67%' }}></div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm font-semibold text-gray-900">Active Frameworks & Standards</p>
              
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Shield className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">SOC 2 Type II</p>
                    <p className="text-xs text-gray-600">Security & availability compliance</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Shield className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">ISO 27001</p>
                    <p className="text-xs text-gray-600">Information security management</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Shield className="size-4 text-gray-700" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">NIST Compliance/CMMC</p>
                      <button
                        onClick={() => setShowCMMCExplanation(true)}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="What is CMMC?"
                      >
                        <FileText className="size-3" />
                      </button>
                      <a
                        href="https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-167.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="NIST SP 800-167 PDF"
                      >
                        <Download className="size-3" />
                      </a>
                      <a
                        href="https://www.nist.gov/itl/ai-risk-management-framework"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        title="NIST AI Risk Management Framework"
                      >
                        <ExternalLink className="size-3" />
                      </a>
                    </div>
                    <p className="text-xs text-gray-600">Cyber Security & PII/PHI</p>
                  </div>
                </div>
                <CheckCircle className="size-4 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Shield className="size-4 text-gray-700" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">HIPAA Ready</p>
                    <p className="text-xs text-gray-600">Healthcare data security</p>
                  </div>
                </div>
                <AlertCircle className="size-4 text-amber-600" />
              </div>
            </div>

            <button className="w-full bg-red-600 text-black py-2.5 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 mb-3">
              View Compliance Details
              <ArrowRight className="size-4" />
            </button>

            <div className="p-2 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs font-semibold text-red-700 mb-0.5">Data Protection:</p>
              <p className="text-xs text-red-600">Comprehensive PII safeguards, cyber security protocols, and industry certifications ensure organizational and customer data security compliance.</p>
            </div>
          </div>
        </div>

        {/* Video Persona Analytics Table */}
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-pink-100 p-3 rounded-lg">
              <Video className="size-6 text-pink-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Video Persona Analytics</h2>
              <p className="text-sm text-gray-600">Watch time and engagement metrics</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Video Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Views</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Total Duration</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Avg Duration</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Feedback</th>
                </tr>
              </thead>
              <tbody>
                {videoPersonaData.map((video, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{video.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{video.views}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{video.totalDuration}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{video.avgDuration}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-green-600">
                          <ThumbsUp className="size-4" />
                          <span className="text-sm font-semibold">{video.thumbsUp}</span>
                        </div>
                        <div className="flex items-center gap-1 text-red-600">
                          <ThumbsDown className="size-4" />
                          <span className="text-sm font-semibold">{video.thumbsDown}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Row: Agent Connections and Configured Context Usage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Agent Connections */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-teal-100 p-3 rounded-lg">
                <Bot className="size-6 text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Agent Connections</h2>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Total Connected</p>
              <p className="text-4xl font-bold text-gray-900">2</p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">Active Agents</p>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <p className="text-sm font-medium text-gray-900">Leadership Agent</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <p className="text-sm font-medium text-gray-900">Workforce Agent</p>
              </div>
            </div>
          </div>

          {/* Configured Context Usage */}
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <Settings className="size-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Configured Context Usage</h2>
            </div>

            <div className="space-y-4">
              {contextUsageData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm font-medium text-gray-700">{item.category}</p>
                    <p className="text-sm font-bold text-gray-900">{item.value}</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full" 
                      style={{ 
                        width: `${(item.value / 24) * 100}%`,
                        backgroundColor: item.color 
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Data Source Notice */}
        <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200">
          <p className="text-sm font-semibold text-gray-900 mb-2">Data Source</p>
          <p className="text-xs text-gray-600 leading-relaxed">
            This dashboard combines sample data with real user interactions. As you and your team use ORA, the sample data will be gradually replaced by actual sentiment tracking, issue reports, topic engagement, keyword analysis, video viewing patterns, agent connections, and configured context usage from your organization. The intent is to provide real, hands-on learning experiences for a 90 day introductory period.
          </p>
        </div>
      </div>

      {/* CMMC Explanation Modal */}
      {showCMMCExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Shield className="size-8 text-blue-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">CMMC Explained</h2>
                    <p className="text-sm text-gray-600">Cybersecurity Maturity Model Certification</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCMMCExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* What is CMMC */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Info className="size-5 text-blue-600" />
                  What is CMMC?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>CMMC</strong> stands for <strong>Cybersecurity Maturity Model Certification</strong>. 
                  It's the U.S. Department of Defense's required cybersecurity standard for any contractor or 
                  subcontractor that handles <strong>Controlled Unclassified Information (CUI)</strong>.
                </p>
              </div>

              {/* Why It Matters */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="size-5 text-blue-600" />
                  Why It Matters
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Mandatory Compliance:</strong> Required for DoD contracts involving CUI</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Third-Party Verification:</strong> Independent assessors validate your security posture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Supply Chain Security:</strong> Protects sensitive defense information across the entire supply chain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Business Requirement:</strong> Without CMMC certification, you cannot bid on certain DoD contracts</span>
                  </li>
                </ul>
              </div>

              {/* CMMC Levels */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="size-5 text-purple-600" />
                  CMMC Maturity Levels
                </h3>
                <div className="space-y-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                      <h4 className="font-bold text-gray-900">Level 1 - Foundational</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Basic cyber hygiene practices (17 controls)</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                      <h4 className="font-bold text-gray-900">Level 2 - Advanced</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Intermediate cyber hygiene (110+ controls, most common for CUI)</p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                      <h4 className="font-bold text-gray-900">Level 3 - Expert</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Advanced/proactive security for high-priority programs</p>
                  </div>
                </div>
              </div>

              {/* Related Standards */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Network className="size-5 text-gray-700" />
                  Related Standards
                </h3>
                <p className="text-sm text-gray-700 mb-2">CMMC builds upon:</p>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <Shield className="size-3 text-gray-600" />
                    <span><strong>NIST SP 800-171:</strong> Protecting CUI in non-federal systems</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="size-3 text-gray-600" />
                    <span><strong>NIST SP 800-172:</strong> Enhanced security for CUI</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="size-3 text-gray-600" />
                    <span><strong>FAR 52.204-21:</strong> Basic safeguarding requirements</span>
                  </li>
                </ul>
              </div>

              {/* Key Takeaway */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="size-5 text-indigo-600" />
                  Key Takeaway
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  If your organization works with the Department of Defense or handles Controlled Unclassified 
                  Information (CUI), CMMC certification is <strong>not optional</strong>—it's a 
                  <strong> contractual requirement</strong>. ORA helps organizations prepare for CMMC compliance 
                  by building AI literacy and security awareness across the workforce.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowCMMCExplanation(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clinger-Cohen Act Explanation Modal */}
      {showClingerCohenExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-cyan-100 p-2 rounded-lg">
                    <DollarSign className="size-8 text-cyan-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">Clinger-Cohen Act Explained</h2>
                    <p className="text-sm text-gray-600">IT Budget Transparency & Accountability</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowClingerCohenExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* What is it */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Info className="size-5 text-blue-600" />
                  What is the Clinger-Cohen Act?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Transparency in IT spending is required to ensure investments are mission aligned and measurable. 
                  The <strong>Information Technology Management Reform Act</strong> and the <strong>Federal Acquisition 
                  Reform Act</strong> were signed into law in 1996 and together became known as the <strong>Clinger‑Cohen Act</strong>.
                </p>
              </div>

              {/* Open Government */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Eye className="size-5 text-blue-600" />
                  Open Government Principles
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  Open government is built on <strong>transparency, participation, and collaboration</strong>, as described 
                  in the Open Government Directive issued by the Office of Management and Budget in 2009 (M-10-06).
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Through implementation of M-10-06, the Freedom of Information Act, the Foundations for Evidence-Based 
                  Policymaking Act of 2018, and other laws and policies governing how information is made available to 
                  the public, the Department works to create an effective government through public awareness and participation.
                </p>
              </div>

              {/* Why It Matters */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="size-5 text-green-600" />
                  Why It Matters for AI Investments
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  Under the Clinger‑Cohen Act, AI spending must be <strong>justified, measurable, mission‑aligned, 
                  and managed as a strategic IT investment</strong>—not a tech experiment. This ensures responsible 
                  stewardship of resources and maximizes return on investment.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Financial Stewardship:</strong> Ensures taxpayer dollars are spent wisely on technology</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Mission Alignment:</strong> IT investments must directly support organizational goals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Measurable Outcomes:</strong> ROI and performance metrics are mandatory</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Strategic Management:</strong> AI is treated as strategic IT, not experimental tech</span>
                  </li>
                </ul>
              </div>

              {/* Transparency & Collaboration */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Users className="size-5 text-purple-600" />
                  Transparency & Collaboration
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Transparency and collaboration are consistent with the <strong>Open Government Act</strong> and the 
                  <strong> Freedom of Information Act</strong>, both of which promote public access to information and 
                  accountable decision‑making. It's not just an AI checkmark — it's <strong>financial stewardship and 
                  responsible citizenship</strong>.
                </p>
              </div>

              {/* Key Dates */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Calendar className="size-5 text-gray-700" />
                  Key Legislative Timeline
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <div className="size-2 bg-blue-600 rounded-full"></div>
                    <span><strong>1996:</strong> Clinger-Cohen Act enacted</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-2 bg-blue-600 rounded-full"></div>
                    <span><strong>2009:</strong> Open Government Directive (M-10-06) issued</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-2 bg-blue-600 rounded-full"></div>
                    <span><strong>2018:</strong> Foundations for Evidence-Based Policymaking Act</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="size-2 bg-blue-600 rounded-full"></div>
                    <span><strong>2022:</strong> Attorney General's FOIA Guidelines (March 2022)</span>
                  </li>
                </ul>
              </div>

              {/* Quote */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                <p className="text-sm text-gray-700 italic leading-relaxed">
                  "Transparency in government operations is a priority."
                </p>
                <p className="text-xs text-gray-600 mt-2 font-semibold">
                  — Attorney General's FOIA Guidelines (March 2022)
                </p>
              </div>

              {/* Key Takeaway */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="size-5 text-cyan-600" />
                  Key Takeaway
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  The Clinger-Cohen Act ensures that AI and IT investments are not treated as experimental technology, 
                  but as <strong>strategic assets</strong> that must demonstrate clear value, mission alignment, and 
                  responsible use of public resources. ORA helps organizations maintain budget transparency and 
                  accountability by tracking AI investment outcomes and ensuring measurable ROI.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowClingerCohenExplanation(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WCAG Explanation Modal */}
      {showWCAGExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gray-100 p-2 rounded-lg">
                    <Eye className="size-8 text-gray-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">WCAG Explained</h2>
                    <p className="text-sm text-gray-600">Web Content Accessibility Guidelines</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowWCAGExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* What is WCAG */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Info className="size-5 text-gray-700" />
                  What is WCAG?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>WCAG</strong> stands for <strong>Web Content Accessibility Guidelines</strong>. 
                  It is an internationally recognized standard developed by the <strong>World Wide Web Consortium (W3C)</strong> 
                  to make web content more accessible to people with disabilities, including visual, auditory, physical, 
                  speech, cognitive, language, learning, and neurological disabilities.
                </p>
              </div>

              {/* Why It Matters */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="size-5 text-gray-700" />
                  Why WCAG Compliance Matters
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Legal Requirement:</strong> Many countries and organizations require WCAG compliance by law</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Inclusive Access:</strong> Ensures people with disabilities can access and use your content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Better UX:</strong> Accessible design improves usability for everyone</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>SEO Benefits:</strong> Accessible sites typically rank better in search engines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Market Reach:</strong> Over 1 billion people worldwide have disabilities</span>
                  </li>
                </ul>
              </div>

              {/* WCAG Levels */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="size-5 text-gray-700" />
                  WCAG Conformance Levels
                </h3>
                <div className="space-y-2">
                  <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-gray-600 text-white rounded-full flex items-center justify-center text-xs font-bold">A</div>
                      <h4 className="font-bold text-gray-900">Level A - Minimum</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Basic level of accessibility - Essential requirements</p>
                  </div>

                  <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-gray-700 text-white rounded-full flex items-center justify-center text-xs font-bold">AA</div>
                      <h4 className="font-bold text-gray-900">Level AA - Recommended</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Deals with common barriers - Most organizations aim for this level</p>
                  </div>

                  <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="size-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">AAA</div>
                      <h4 className="font-bold text-gray-900">Level AAA - Highest</h4>
                    </div>
                    <p className="text-xs text-gray-700 ml-8">Enhanced accessibility - Recommended for specialized content</p>
                  </div>
                </div>
              </div>

              {/* WCAG Principles (POUR) */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="size-5 text-gray-700" />
                  Four Principles of WCAG (POUR)
                </h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">1. Perceivable</h4>
                    <p className="text-sm text-gray-700">Information and user interface components must be presentable to users in ways they can perceive (e.g., text alternatives for images, captions for videos).</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">2. Operable</h4>
                    <p className="text-sm text-gray-700">User interface components and navigation must be operable (e.g., keyboard accessible, sufficient time to read, seizure-free design).</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">3. Understandable</h4>
                    <p className="text-sm text-gray-700">Information and operation of the user interface must be understandable (e.g., readable text, predictable behavior, input assistance).</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">4. Robust</h4>
                    <p className="text-sm text-gray-700">Content must be robust enough to be interpreted reliably by a wide variety of user agents, including assistive technologies.</p>
                  </div>
                </div>
              </div>

              {/* Official W3C Resources */}
              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Network className="size-5 text-gray-700" />
                  Official W3C WCAG Resources
                </h3>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">WCAG Overview (Best Starting Point)</h4>
                    <p className="text-xs text-gray-700 mb-2">Explains what WCAG is, who it's for, and links to all versions.</p>
                    <a 
                      href="https://www.w3.org/WAI/standards-guidelines/wcag/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline font-medium break-all"
                    >
                      https://www.w3.org/WAI/standards-guidelines/wcag/
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">WCAG 2.2 Standard (Latest Version)</h4>
                    <p className="text-xs text-gray-700 mb-2">The full technical specification for WCAG 2.2 - Current standard.</p>
                    <a 
                      href="https://www.w3.org/TR/WCAG22/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline font-medium break-all"
                    >
                      https://www.w3.org/TR/WCAG22/
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">How to Meet WCAG (Quick Reference)</h4>
                    <p className="text-xs text-gray-700 mb-2">Customizable quick reference to WCAG requirements and techniques.</p>
                    <a 
                      href="https://www.w3.org/WAI/WCAG22/quickref/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline font-medium break-all"
                    >
                      https://www.w3.org/WAI/WCAG22/quickref/
                    </a>
                  </div>
                </div>
              </div>

              {/* ORA's WCAG Compliance */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="size-5 text-gray-700" />
                  ORA's WCAG Compliance
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  All ORA Standard Operating Procedures (SOPs), documentation, and training materials are developed 
                  following <strong>WCAG 2.2 Level AA guidelines</strong> to ensure accessibility for all users, 
                  including those using assistive technologies.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Screen reader compatible</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Keyboard navigable</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Sufficient color contrast</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Resizable text</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Alternative text for images</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Captions and transcripts</span>
                  </div>
                </div>
              </div>

              {/* Key Takeaway */}
              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="size-5 text-gray-700" />
                  Key Takeaway
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  WCAG compliance isn't just about meeting legal requirements—it's about creating an inclusive 
                  digital experience that serves everyone effectively. By following WCAG guidelines, ORA ensures 
                  that AI adoption training and resources are accessible to all team members, regardless of ability, 
                  supporting truly equitable organizational transformation.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowWCAGExplanation(false)}
                className="w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-black transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Semantic Context Explanation Modal */}
      {showSemanticExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <Brain className="size-8 text-indigo-700 fill-yellow-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">Semantic Context Explained</h2>
                    <p className="text-sm text-gray-600">AI Understanding & Contextual Awareness</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSemanticExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* What is Semantic Context */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Brain className="size-5 text-indigo-600 fill-yellow-400" />
                  What is Semantic Context?
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Semantic Context refers to an AI system's ability to understand the <strong>meaning, intent, and relationships</strong> within information, 
                  rather than just processing words or data literally. It's how AI understands the "why" behind the "what" - connecting organizational knowledge, 
                  business processes, and user intent to provide relevant, contextually appropriate responses.
                </p>
              </div>

              {/* Why It Matters */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="size-5 text-blue-600" />
                  Why Semantic Context Matters
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Reduces Hallucinations:</strong> AI that understands context is less likely to generate false or irrelevant information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Improves Accuracy:</strong> Contextual understanding ensures responses align with organizational knowledge and business rules</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Enhances User Experience:</strong> Employees get relevant, actionable answers that match their specific situation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Maintains Governance:</strong> Context-aware AI respects boundaries, permissions, and organizational policies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Enables Better Decisions:</strong> When AI understands relationships between data, it can provide insights, not just answers</span>
                  </li>
                </ul>
              </div>

              {/* Key Components */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="size-5 text-indigo-600" />
                  Key Components of Semantic Context
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-indigo-600 rounded-full"></div>
                      Knowledge Graphs & Ontologies
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Structured representations of how concepts, entities, and data relate to each other within your organization
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-indigo-600 rounded-full"></div>
                      Metadata & Tagging
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Rich metadata that describes content, documents, and data - who created it, when, for what purpose, and how it connects to other information
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-indigo-600 rounded-full"></div>
                      Embedding Models & Vector Databases
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Technologies that capture semantic meaning in mathematical representations, enabling AI to find conceptually similar information even when exact words differ
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-indigo-600 rounded-full"></div>
                      Domain-Specific Context
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Industry terminology, organizational structure, business processes, and institutional knowledge that gives AI awareness of your specific environment
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-indigo-600 rounded-full"></div>
                      Retrieval-Augmented Generation (RAG)
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Systems that retrieve relevant organizational knowledge before generating responses, grounding AI outputs in verified information
                    </p>
                  </div>
                </div>
              </div>

              {/* How It's Measured */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="size-5 text-green-600" />
                  How We Measure Semantic Context (94%)
                </h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="leading-relaxed text-gray-600">
                    Your organization's <strong>94% Semantic Context score</strong> reflects strong implementation across several key areas:
                  </p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Metadata coverage across critical documents and data sources</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Quality and accuracy of AI-generated responses based on organizational context</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Implementation of RAG systems with validated knowledge bases</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">User satisfaction with relevance and accuracy of AI responses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Reduction in AI hallucinations and off-topic responses</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Real-World Example */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="size-5 text-purple-600" />
                  Real-World Example
                </h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">❌ Without Semantic Context:</p>
                    <p className="italic text-gray-600 bg-white p-2 rounded border border-purple-200">
                      "What's our policy on remote work?" → AI returns generic remote work advice from the internet or a policy from 2019 that's no longer current.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">✅ With Semantic Context:</p>
                    <p className="italic text-gray-600 bg-white p-2 rounded border border-purple-200">
                      "What's our policy on remote work?" → AI understands you work in Engineering, retrieves the current 2026 Remote Work Policy (v3.2), 
                      knows you're based in California (different tax implications), and provides the specific approval workflow for your department, 
                      including a link to the VPN setup guide and your manager's name for approval requests.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Note */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Bottom Line:</strong> Semantic Context is the difference between an AI that processes text 
                  and an AI that truly <em>understands</em> your organization. It transforms AI from a generic chatbot into an intelligent assistant 
                  that speaks your language, knows your business, and respects your boundaries. ORA's high score (94%) demonstrates sophisticated 
                  context management that makes AI interactions more accurate, relevant, and trustworthy for your team.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowSemanticExplanation(false)}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Explanation Modal */}
      {showSecurityExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <Shield className="size-8 text-red-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">AI Security Explained</h2>
                    <p className="text-sm text-gray-600">OPSEC Practices & Cybersecurity Frameworks</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSecurityExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* What is AI Security */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="size-5 text-red-600" />
                  What is AI Security?
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  AI Security encompasses the practices, policies, and technologies that protect AI systems from cyber threats, ensure operational security (OPSEC), 
                  and align with federal cybersecurity frameworks. It addresses unique risks including adversarial attacks, data poisoning, model theft, 
                  and unauthorized access to AI systems and their underlying data.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Lock className="size-4 text-orange-600" />
                    What is OPSEC?
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>OPSEC (Operations Security)</strong> is a systematic process that identifies critical information, analyzes threats, assesses vulnerabilities, 
                    and applies countermeasures to protect sensitive operations from adversaries. In the context of AI systems, OPSEC focuses on preventing unauthorized 
                    disclosure of AI capabilities, training data, model parameters, system architecture, and usage patterns that could be exploited by malicious actors. 
                    It answers the question: "What information, if known by adversaries, could compromise our AI systems and organizational security?"
                  </p>
                </div>
              </div>

              {/* Why It Matters */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="size-5 text-red-600" />
                  Why AI Security Matters
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Protects Critical Infrastructure:</strong> AI systems often have access to sensitive data and control critical organizational functions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Prevents Data Breaches:</strong> Proper OPSEC prevents unauthorized access to training data, model parameters, and AI-generated outputs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Ensures Compliance:</strong> Aligns with federal cybersecurity requirements including RMF (Risk Management Framework)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Maintains Trust:</strong> Security incidents can erode user confidence and damage organizational reputation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>National Defense Priority:</strong> AI security is critical to America's AI Action Plan and national competitiveness</span>
                  </li>
                </ul>
              </div>

              {/* Key Federal Policies */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="size-5 text-red-600" />
                  Key Federal AI Security Policies
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-300 rounded-lg">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 border-b border-gray-300 font-bold text-gray-900">Policy</th>
                        <th className="text-left p-3 border-b border-gray-300 font-bold text-gray-900">Year</th>
                        <th className="text-left p-3 border-b border-gray-300 font-bold text-gray-900">What It Covers</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-200">
                        <td className="p-3 font-semibold text-gray-900">DoD AI Cybersecurity Risk Management Tailoring Guide</td>
                        <td className="p-3 text-gray-600">2025</td>
                        <td className="p-3 text-gray-600">AI cybersecurity, OPSEC protections, RMF alignment</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="p-3 font-semibold text-gray-900">CISA – Recent U.S. Efforts on AI Policy</td>
                        <td className="p-3 text-gray-600">2023–2025</td>
                        <td className="p-3 text-gray-600">Federal AI governance, DHS AI policy, critical infrastructure security</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold text-gray-900">America's AI Action Plan</td>
                        <td className="p-3 text-gray-600">2025</td>
                        <td className="p-3 text-gray-600">National AI strategy, AI security, national defense priorities</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* OPSEC Practices */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Lock className="size-5 text-red-600" />
                  Critical OPSEC Practices for AI
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-red-600 rounded-full"></div>
                      Access Control & Authentication
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Role-based access control (RBAC), multi-factor authentication, and principle of least privilege for AI system access
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-red-600 rounded-full"></div>
                      Data Protection & Encryption
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Encryption at rest and in transit, secure data pipelines, and protection of training data and model parameters
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-red-600 rounded-full"></div>
                      Model Security & Integrity
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Protection against adversarial attacks, model poisoning, and unauthorized model extraction or tampering
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-red-600 rounded-full"></div>
                      Audit Logging & Monitoring
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Comprehensive logging of AI system access, queries, outputs, and security events for forensic analysis
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="size-2 bg-red-600 rounded-full"></div>
                      Vendor & Supply Chain Security
                    </h4>
                    <p className="text-xs text-gray-700 ml-4">
                      Vetting third-party AI providers, secure API integrations, and monitoring for supply chain vulnerabilities
                    </p>
                  </div>
                </div>
              </div>

              {/* How It's Measured */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="size-5 text-green-600" />
                  How We Measure Security (100%)
                </h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="leading-relaxed text-gray-600">
                    Your organization's <strong>100% Security score</strong> reflects comprehensive implementation of AI cybersecurity best practices:
                  </p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Full implementation of RMF-aligned security controls for AI systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Encryption of all AI training data, model parameters, and user queries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">RBAC implementation with multi-factor authentication for all AI access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Comprehensive audit logging and real-time security monitoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Regular security assessments and penetration testing of AI systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-600">Vendor security compliance verification and supply chain risk management</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Real-World Example */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="size-5 text-purple-600" />
                  Real-World Security Scenario
                </h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">❌ Without Proper Security:</p>
                    <p className="italic text-gray-600 bg-white p-2 rounded border border-purple-200">
                      An employee with basic credentials can access sensitive AI model outputs, export training data, or use the AI to process classified information. 
                      No audit trail exists. A compromised API key provides unlimited access to the AI system.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">✅ With Strong Security (Your Organization):</p>
                    <p className="italic text-gray-600 bg-white p-2 rounded border border-purple-200">
                      All AI access requires MFA and role-based permissions. Queries are encrypted end-to-end. An employee attempting to access restricted data 
                      triggers an automated alert and requires supervisor approval. All interactions are logged with user identity, timestamp, and data classification. 
                      Model parameters are encrypted and access-controlled. Regular security audits ensure compliance with DoD AI Cybersecurity guidelines and America's AI Action Plan.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Note */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Bottom Line:</strong> AI Security is not just traditional cybersecurity applied to AI—it requires specialized 
                  OPSEC practices that address unique AI risks including adversarial attacks, data poisoning, and model theft. Your organization's perfect 100% score 
                  demonstrates alignment with cutting-edge federal guidance including the DoD AI Cybersecurity Risk Management Tailoring Guide, CISA's AI policy framework, 
                  and America's AI Action Plan. This ensures ORA is protected to the highest standards while supporting national defense and critical infrastructure priorities.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowSecurityExplanation(false)}
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-red-700 hover:to-orange-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transparency Explanation Modal */}
      {showTransparencyExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Eye className="size-8 text-blue-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">Transparency Explained</h2>
                    <p className="text-sm text-gray-600">FOIA, Open Government & Accountability</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTransparencyExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* What is Transparency */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Eye className="size-5 text-blue-600" />
                  What is Government Transparency?
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  Government transparency ensures the public has access to information about government operations, decisions, and AI system usage. 
                  It's a cornerstone of democratic accountability and builds trust between government agencies and the citizens they serve.
                </p>
              </div>

              {/* Key Laws & Policies */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="size-5 text-blue-600" />
                  Key Transparency Laws
                </h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-gray-900">Freedom of Information Act (FOIA):</strong>
                      <span className="text-gray-600"> Enacted in 1967, FOIA gives the public the right to request access to federal agency records, ensuring government accountability and transparency.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-gray-900">Open Government Act:</strong>
                      <span className="text-gray-600"> Strengthened FOIA by improving public access to government information and establishing the Office of Government Information Services (OGIS).</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-gray-900">Office of Information Policy (OIP):</strong>
                      <span className="text-gray-600"> Part of the Department of Justice, OIP provides guidance on FOIA compliance and promotes transparency across federal agencies.</span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Official Resource */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <ExternalLink className="size-5 text-blue-600" />
                  Official Government Resource
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Learn more about FOIA, transparency policies, and how the Department of Justice ensures government accountability:
                </p>
                <a
                  href="https://www.justice.gov/oip/government-transparency"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-800 underline hover:text-blue-900 font-semibold"
                >
                  Department of Justice - Office of Information Policy
                  <ExternalLink className="size-4" />
                </a>
              </div>

              {/* Why It Matters for AI */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Brain className="size-5 text-blue-600 fill-yellow-400" />
                  Why Transparency Matters for AI
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Algorithmic Accountability:</strong> Citizens have the right to understand how AI systems make decisions that affect them</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Public Trust:</strong> Transparency in AI usage builds confidence in government technology initiatives</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Bias Detection:</strong> Open documentation helps identify and address potential biases in AI systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600"><strong>Legal Compliance:</strong> FOIA requests may include AI system documentation and decision records</span>
                  </li>
                </ul>
              </div>

              {/* Bottom Line */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Bottom Line:</strong> Transparency isn't optional—it's a legal requirement and democratic obligation. 
                  Your organization's 70% transparency score reflects progress in documenting AI usage, maintaining accessible records, and preparing for FOIA requests. 
                  Continue improving by documenting AI decision processes, maintaining audit trails, and ensuring all team members understand transparency obligations under FOIA and the Open Government Act.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowTransparencyExplanation(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance/Credentials & Certifications Explanation Modal */}
      {showComplianceExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b-2 border-gray-300 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Shield className="size-8 text-green-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-black">Credentials & Certifications</h2>
                    <p className="text-sm text-gray-600">Cyber security, PII protection & compliance</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowComplianceExplanation(false)}
                  className="text-gray-500 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FileCheck className="size-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Overall Score */}
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Overall Compliance Score</h3>
                <div className="text-6xl font-bold text-green-700 mb-2">97%</div>
                <p className="text-sm text-gray-600">Certified & Compliant</p>
              </div>

              {/* Certification Status */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="size-5 text-green-600" />
                  Certification Status
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-green-50 border border-green-300 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-700">12</div>
                    <p className="text-sm text-gray-700 font-medium mt-1">Certified</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">4</div>
                    <p className="text-sm text-gray-700 font-medium mt-1">In Progress</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-700">2</div>
                    <p className="text-sm text-gray-700 font-medium mt-1">Pending</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-gray-700">67%</div>
                    <p className="text-sm text-gray-700 font-medium mt-1">Complete</p>
                  </div>
                </div>
              </div>

              {/* Active Frameworks & Standards */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="size-5 text-blue-600" />
                  Active Frameworks & Standards
                </h3>
                <div className="space-y-3">
                  {/* SOC 2 Type II */}
                  <div className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="size-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">SOC 2 Type II</h4>
                        <p className="text-sm text-gray-600">Security & availability compliance</p>
                      </div>
                    </div>
                  </div>

                  {/* ISO 27001 */}
                  <div className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="size-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">ISO 27001</h4>
                        <p className="text-sm text-gray-600">Information security management</p>
                      </div>
                    </div>
                  </div>

                  {/* NIST Compliance/CMMC */}
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="size-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">NIST Compliance/CMMC</h4>
                        <p className="text-sm text-gray-600 mb-2">Cyber Security & PII/PHI protection</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white rounded p-2 border border-blue-200">
                            <span className="font-semibold text-gray-900">CMMC:</span>
                            <span className="ml-1 text-blue-700 font-bold">94%</span>
                          </div>
                          <div className="bg-white rounded p-2 border border-blue-200">
                            <span className="font-semibold text-gray-900">RBAC:</span>
                            <span className="ml-1 text-green-700 font-bold">100%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* HIPAA Ready */}
                  <div className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="size-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">HIPAA Ready</h4>
                        <p className="text-sm text-gray-600">Healthcare data security</p>
                      </div>
                    </div>
                  </div>

                  {/* RBAC Audits */}
                  <div className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="size-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">RBAC Audits</h4>
                        <p className="text-sm text-gray-600">Role-Based Access Control implementation and continuous monitoring</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Note */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Your Organization's Commitment:</strong> With 12 active certifications and 67% completion rate across 
                  all frameworks, your organization demonstrates robust cybersecurity posture and compliance with industry-leading standards. The strong 
                  CMMC (94%) and perfect RBAC (100%) scores ensure AI systems are protected according to federal guidelines and best practices for 
                  sensitive data handling.
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowComplianceExplanation(false)}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-colors shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Required Overlay */}
      {requiresAuth && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8">
            <div className="text-center">
              {/* Lock Icon Badge */}
              <div className="mb-6">
                <div className="inline-block p-4 bg-purple-100 rounded-full">
                  <Lock className="size-8 text-purple-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-black mb-2">Account Required</h2>
              <p className="text-gray-600 mb-6">Authenticate to access the Analytics Dashboard</p>
              
              {/* Error Message */}
              {authError && (
                <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <p className="text-sm text-red-700 text-left">
                    <span className="font-semibold">Authentication Failed:</span>
                    <br/>{authError}
                  </p>
                </div>
              )}

              {/* Google Login Button */}
              <button
                onClick={() => handleGoogleAuth('carapaulson1@gmail.com')}
                disabled={isAuthenticating}
                className="w-full mb-4 flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isAuthenticating ? (
                  <>
                    <div className="animate-spin">
                      <svg className="size-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-900">Authenticating...</span>
                  </>
                ) : (
                  <>
                    <svg className="size-5" viewBox="0 0 24 24">
                      <path fill="#1f2937" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#1f2937" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#1f2937" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#1f2937" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="font-semibold text-gray-900">Authenticate with Google</span>
                  </>
                )}
              </button>

              {/* Alternative Authentication Methods */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-3">Or authenticate directly:</p>
                <button
                  onClick={() => handleGoogleAuth('cara@oratf.info')}
                  disabled={isAuthenticating}
                  className="w-full mb-2 px-4 py-2 text-sm bg-purple-50 border border-purple-200 text-purple-700 rounded hover:bg-purple-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-medium"
                >
                  Log in as cara@oratf.info
                </button>
                <button
                  onClick={() => {
                    const customEmail = prompt('Enter your @oratf.info email:');
                    if (customEmail) {
                      handleGoogleAuth(customEmail);
                    }
                  }}
                  disabled={isAuthenticating}
                  className="w-full px-4 py-2 text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-medium"
                >
                  Log in with @oratf.info email
                </button>
              </div>

              {/* Whitelisted Accounts Info */}
              <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="text-sm text-gray-700 text-left">
                  <span className="font-semibold text-blue-900">Whitelisted Accounts:</span>
                  <br/>• carapaulson1@gmail.com
                  <br/>• cara@oratf.info
                  <br/>• any @oratf.info email
                </p>
              </div>

              {/* Go Back Button */}
              <button
                onClick={() => {
                  setRequiresAuth(false);
                  setAuthError(null);
                  setShowDashboard(true);
                }}
                disabled={isAuthenticating}
                className="w-full mt-6 px-6 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
}